import { useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';

export interface UploadedFile {
  id: string;
  filename: string;
  mimetype: string;
  size: number;
  kategorie: string;
}

interface FileUploadProps {
  uploads: UploadedFile[];
  onUpload: (files: File[], kategorie: string) => Promise<void>;
  onRemove: (id: string) => Promise<void>;
  uploading: boolean;
}

const KATEGORIE_VALUES = ['ausweis', 'vollmacht', 'korrespondenz', 'beweis', 'sonstiges'] as const;

const ALLOWED = ['.pdf', '.jpg', '.jpeg', '.png', '.heic', '.zip'];

export function FileUpload({ uploads, onUpload, onRemove, uploading }: FileUploadProps) {
  const { t } = useTranslation();
  const inputRef = useRef<HTMLInputElement>(null);
  const [kategorie, setKategorie] = useState<string>('sonstiges');
  const [error, setError] = useState<string | null>(null);

  const labelForKategorie = (k: string): string =>
    (KATEGORIE_VALUES as readonly string[]).includes(k) ? t(`upload.kategorien.${k}`) : k;

  const handleFiles = async (files: FileList | null) => {
    setError(null);
    if (!files || files.length === 0) return;
    const list = Array.from(files);
    const tooBig = list.find((f) => f.size > 10 * 1024 * 1024);
    if (tooBig) {
      setError(t('upload.tooBig', { name: tooBig.name }));
      return;
    }
    try {
      await onUpload(list, kategorie);
      if (inputRef.current) inputRef.current.value = '';
    } catch (err) {
      setError(t('upload.failed'));
    }
  };

  return (
    <div className="upload">
      <div className="field">
        <label className="field-label" htmlFor="kategorie">{t('upload.kategorieLabel')}</label>
        <select
          id="kategorie"
          className="field-input"
          value={kategorie}
          onChange={(e) => setKategorie(e.target.value)}
        >
          {KATEGORIE_VALUES.map((k) => (
            <option key={k} value={k}>{t(`upload.kategorien.${k}`)}</option>
          ))}
        </select>
      </div>

      <div className="upload-dropzone">
        <input
          ref={inputRef}
          type="file"
          accept={ALLOWED.join(',')}
          multiple
          disabled={uploading}
          onChange={(e) => handleFiles(e.target.files)}
        />
        <p className="upload-hint">{t('upload.dropHint')}</p>
      </div>

      {error && <div className="alert alert-error">{error}</div>}

      {uploads.length > 0 && (
        <ul className="upload-list">
          {uploads.map((u) => (
            <li key={u.id} className="upload-item">
              <div>
                <div className="upload-item-name">{u.filename}</div>
                <div className="upload-item-meta">
                  {labelForKategorie(u.kategorie)} · {formatBytes(u.size)}
                </div>
              </div>
              <button
                type="button"
                className="upload-remove"
                onClick={() => onRemove(u.id)}
                aria-label={t('upload.removeAria', { name: u.filename })}
              >
                {t('upload.remove')}
              </button>
            </li>
          ))}
        </ul>
      )}
    </div>
  );
}

function formatBytes(b: number): string {
  if (b < 1024) return `${b} B`;
  if (b < 1024 * 1024) return `${(b / 1024).toFixed(1)} KB`;
  return `${(b / 1024 / 1024).toFixed(1)} MB`;
}
